<template>
    <div>
        <div :class="['course-info', theme]">
            <div class="course-info__item -duration" v-if="showVideoDuration">
                <span class="course-info__label" v-html="$t('General.duration')"></span>
                <span class="course-info__data">{{ videoDuration }}</span>

            </div>

            <div class="course-info__item -level">
                <span class="course-info__label" v-html="$t('General.level')"></span>
                <span class="course-info__data -clickable" @click="goToListWithPreFilteredOption(levelObject, 'difficulty_levels')">
                    {{ $t(`Select.difficulty_level_note_${level}`) }}
                </span>
            </div>

            <span v-if="isTestAvailable" class="course-info__coursetest">
                <span class="icon-file-check"></span>
                <span v-html="$t('General.has_coursetest')"></span>
            </span>

            <div class="course-info__item -categories" v-if="categories.length">
                <span class="course-info__label" v-html="$t('General.categories')"></span>
                <div
                    class="course-info__category-with-ancestors"
                    :key="index"
                    v-for="(categoryWithAncestors, index) in categories"
                >
                <span class="course-info__data -clickable" v-for="(category, index) in categoryWithAncestors"
                      :key="index"
                      @click="goToListWithPreFilteredOption(category, 'categories')"
                >
                    <span>{{ category.label }}</span>
                    <template v-if="index < categoryWithAncestors.length - 1"> > </template>
                </span>
                    <template v-if="index < categories.length - 1">,</template>
                </div>
            </div>
            <template v-if="$totemLang !== 'dk'">
                <div class="course-info__item -highlight -updateddate" v-if="lastUpdatedDate">
                    <span class="course-info__icon icon-calendar-check"></span>
                    <span class="course-info__label" v-html="$t('CourseDates.last_updated')"></span>
                    <span class="course-info__description">{{ formattedDateForCountry(lastUpdatedDate) }}</span>
                </div>
                <div class="course-info__item -publishdate" v-if="publishedDate && $totemLang">
                    <span class="course-info__icon icon-calendar-star"></span>
                    <span class="course-info__label" v-html="$t('CourseDates.publish_date')"></span>
                    <span class="course-info__description">{{ formattedDateForCountry(publishedDate) }}</span>
                </div>
                <div class="course-info__item - expirationdate" v-if="expirationDate">
                    <span class="course-info__icon icon-calendar-xmark"></span>
                    <span class="course-info__label" v-html="$t('CourseDates.expiration_date')"></span>
                    <span class="course-info__description">{{ formattedDateForCountry(expirationDate) }}</span>
                </div>
            </template>
        </div>

        <div :class="['course-info', theme]" v-if="showCertificatesOrDurations">
            <span class="course-info__badges" v-if="shouldShowCertificates && certifiedByLength > 0">
                <span class="course-info__label" v-html="$t('General.authorization')"></span>
                <TotemCertificationBadge
                    v-for="(cert, index) in certifiedByObjectArray"
                    :name="cert.label"
                    :key="'certs-index' + index"
                    size="small"
                    @click.native="goToListWithPreFilteredOption(cert, 'authorizations')"
                />
            </span>

            <span class="course-info__authorization-hours" v-if="durations && durations.length > 0">
                  <span class="course-info__label" v-html="$t('General.authorization')"></span>
                     <TotemAuthorizationHours :durations="durations" direction="row" />
                <TotemAuthorizationId
                    :id="authorizationId"
                    v-if="authorizationId"
                />
            </span>
        </div>
        <ModalCourseRating
            :courseTitle="title"
            :courseId="course.id"
            @ratingSaved="showToast"
        />
        <TotemToast ref="toast"/>
    </div>
</template>

<script>
    import goToListWithPreFilteredOption from 'components/shared/mixins/goToListWithPreFilteredOption'
    import ModalCourseRating from 'components/shared/modals/ModalCourseRating'
    import courseRatingHelper from 'components/shared/global-courses-items/helper/course-rating-helper'
    import convertMinutesToHours from 'components/shared/mixins/convertMinutesToHours'
    import { mapGetters } from 'vuex'
    import { formattedDateForCountry } from 'components/shared/mixins/dateHelper'

    export default {
        name: 'totem-course-info',
        components: { ModalCourseRating },
        mixins: [ goToListWithPreFilteredOption, courseRatingHelper, convertMinutesToHours ],
        props: {
            categories: {
                type: Array,
                default: () => []
            },
            level: [ Number, String ],
            isLive: Boolean,
            liveEventDate: String,
            liveEventTime: String,
            authorizationId: String,
            certifiedBy: Array,
            durations: Array,
            title: String,
            theme: {
                type: String,
                validator: (prop) => ['basic', 'highlight'].includes(prop),
                default: 'basic'
            },
            isTestAvailable: Boolean,
            course: {
                type: Object
            },
            publishedDate: String,
            expirationDate: String,
            lastUpdatedDate: String,
            showVideoDuration: {
                type: Boolean,
                default: true
            }
        },
        computed: {
            ...mapGetters({
                ratingSaved: 'Ratings/getRatingSavedState',
                isDigitalClassRoomTraining: 'Courses/isDigitalClassRoomTraining',
                digitalClassRoomTrainingByCourseId: 'Courses/getDigitalClassRoomEventsWithCourseId'
            }),
            shouldShowCertificates () {
                return this.$totemLang !== 'se'
            },
            certificationBadgeSize () {
                return this.theme === 'basic' ? 'small' : 'medium'
            },
            levelObject () {
                return {
                    id: this.level,
                    label: this.level
                }
            },
            certifiedByObjectArray () {
                return this.certifiedBy.map(el => ({
                    id: el.label,
                    label: el.label
                }))
            },
            certifiedByLength () {
                return this.certifiedBy?.length
            },
            showCertificatesOrDurations () {
                return (this.shouldShowCertificates && this.certifiedByLength > 0) || (this.durations && this.durations.length > 0)
            },
            videoDuration () {
                if (this.isDigitalClassRoomTraining(this.course)) {
                    const event = this.digitalClassRoomTrainingByCourseId(this.course.id)

                    if (event) {
                        const sessionsLength = event.sessions?.length || 0

                        if (sessionsLength > 1) {
                            return `${sessionsLength} ${this.$t('DigitalClassrooms.days')}`
                        }
                    }

                    const { videoDuration } = this.course

                    const days = Math.floor(videoDuration / 420)
                    const remainingMinutes = videoDuration % 420
                    const hours = Math.floor(remainingMinutes / 60)
                    const minutes = remainingMinutes % 60

                    return days
                        ? `${days} ${this.$t('DigitalClassrooms.days')}${hours ? ` ${hours} ${this.$t('DigitalClassrooms.hours')}` : ''}${minutes ? ` ${minutes} ${this.$t('DigitalClassrooms.minutes')}` : ''}`
                        : hours
                            ? `${hours} ${this.$t('DigitalClassrooms.hours')}${minutes ? ` ${minutes} ${this.$t('DigitalClassrooms.minutes')}` : ''}`
                            : `${minutes} ${this.$t('DigitalClassrooms.minutes')}`
                }

                const { videoDuration } = this.course
                if (videoDuration % 60 === 0) {
                    return `${videoDuration / 60} ${this.$t('DigitalClassrooms.hours')}`
                }

                if (videoDuration < 60) {
                    return `${videoDuration} ${this.$t('DigitalClassrooms.minutes')}`
                }

                return this.convertMinutesToHours(videoDuration || 0)
            }
        },
        methods: {
            showAllCategories () {
                this.showCategories = true
            },
            hideAllCategories () {
                this.showCategories = false
            },
            showToast () {
                this.$refs.toast.show(this.$t('Rating.thank_you_message'))
            },
            formattedDateForCountry
        }
    }
</script>

<style lang="scss" scoped>
    .course-info {
        font-size: 1.4rem;
        pointer-events: none;
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        gap: 10px;

        &__icon {
            margin: 0 6px 0 0;
        }

        &__category-with-ancestors {
            padding-right: 5px;
        }
        &__category-with-ancestors:last-child {
            padding-right: 0;
        }

        &__item {
            cursor: pointer;
            display: inline;
            align-items: center;

            &.-categories {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                position: relative;

                [class*="mobile"] & {
                    display: flex;
                    word-wrap: break-word;
                    flex-direction: row;
                }
            }

            &.-level {
                display: flex;
                position: relative;
            }
        }

        &__label {
            font-family: PlusJakartaSans-SemiBold, sans-serif;
            line-height: 150%;
            padding: 0 3px 0 0;
        }

        &__data {
            font-family: PlusJakartaSans-Regular, sans-serif;

            &.-clickable {
                cursor: pointer;
            }

            &.-categories {
                text-decoration: underline;
                color: $lavender;
            }
        }

        &__category-name {
            margin: 0 0 0 5px;
        }

        &__description {
            font-size: 1.5rem;
        }

        &__additional-labels {
            display: flex;
            align-items: center;
        }

        &__badges,
        &__authorization-hours {
            display: inline-flex;
            gap: 6px;
            margin: 6px 0;
        }

        &__coursetest {
            white-space: nowrap;
            display: flex;
            align-items: center;
            position: relative;
            margin: 0 10px 0 0;
            font-family: PlusJakartaSans-Regular, sans-serif;

            .icon-file-check {
                margin: 0 3px 0 0;
            }

            [class*="mobile_sm"] & {
                margin: 0;
            }

            [class*="desktop"] & {
                flex-direction: row;
            }
        }

        .icon-file-text {
            margin: 0 6px 0 0;
        }

        &.highlight {
            display: flex;
            font-size: 1.5rem;
            color: $black;
            pointer-events: all;
            flex-direction: row;
            gap: 12px;

            [class*="mobile"] & {
                flex-direction: column;
            }

            .course-info__item {
                font-size: 1.6rem;
                margin: 0;
                padding: 0;

                [class*="desktop"] & {
                    &.-rating {
                        border: none;
                    }

                    &.-badges {
                        display: block;
                    }
                }
            }

            .course-info__data {
                font-family: PlusJakartaSans-Regular, sans-serif;
                font-size: 1.6rem;
                margin: 0 0 0 5px;
            }

            .course-info__additional-labels {
                padding: 25px 40px;

                [class*="desktop"] & {
                    margin-left: auto;
                }
            }

            .course-info__badges {
                border-bottom: 1px solid $white;
                [class*="desktop"] & {
                    border: none;
                }
            }


            @keyframes pulse {
                0% {
                    transform: scale(0.95);
                    box-shadow: 0 0 0 0 rgba(240, 125, 0, 0.7);
                }

                70% {
                    transform: scale(1.1);
                    box-shadow: 0 0 0 10px rgba(240, 125, 0, 0);
                }

                100% {
                    transform: scale(0.95);
                    box-shadow: 0 0 0 0 rgba(240, 125, 0, 0);
                }
            }
        }
    }
</style>
